/**
 * Respostas rápidas. Da loja (gerência cria, todas usam) ou pessoais (cada
 * vendedora cria as suas). Uso no chat: digite "/" e o atalho.
 */
import type { RespostaRapida } from "@ydchat/shared";
import { VARIAVEIS_RESPOSTA, veOperacaoInteira } from "@ydchat/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { Esqueleto } from "@/components/ui/esqueleto";
import { Interruptor } from "@/components/ui/interruptor";
import { Rotulo } from "@/components/ui/seletor";
import { Selo } from "@/components/ui/selo";
import { api, ErroApi } from "@/lib/api";
import { useRespostasRapidas } from "@/lib/central";
import { useUsuario } from "@/lib/sessao";

export function GerenciarRespostas() {
  const eu = useUsuario();
  const gestor = veOperacaoInteira(eu.papel);
  const respostas = useRespostasRapidas();
  const [editando, setEditando] = useState<RespostaRapida | "nova" | null>(null);
  const qc = useQueryClient();
  const remover = useMutation({
    mutationFn: (id: string) => api.del(`/respostas-rapidas/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["respostas-rapidas"] }),
    onError: (e) => toast.error(e instanceof ErroApi ? e.message : "Não foi possível remover."),
  });

  if (editando) {
    return (
      <FormResposta
        inicial={editando === "nova" ? null : editando}
        podeLoja={gestor}
        onFim={() => setEditando(null)}
      />
    );
  }
  const editavel = (r: RespostaRapida) => r.pessoal || gestor;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-neutral-600">
          No chat, digite <kbd className="rounded border border-neutral-200 px-1">/</kbd> e o
          atalho. Variáveis: {Object.keys(VARIAVEIS_RESPOSTA).join(", ")}.
        </p>
        <Botao tamanho="sm" onClick={() => setEditando("nova")}>
          <Plus size={16} strokeWidth={1.75} /> Nova
        </Botao>
      </div>
      {respostas.isPending ? (
        <Esqueleto className="h-24" />
      ) : !respostas.data?.length ? (
        <p className="rounded-lg border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500">
          Nenhuma resposta ainda. Comece pelas perguntas que mais aparecem: PIX, prazo de entrega,
          trocas.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100 rounded-xl border border-neutral-200 bg-white">
          {respostas.data.map((r) => (
            <li key={r.id} className="flex items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-neutral-900">/{r.atalho}</span>
                  <span className="text-neutral-600">{r.titulo}</span>
                  <Selo tom={r.pessoal ? "neutro" : "dourado"}>
                    {r.pessoal ? "Pessoal" : "Da loja"}
                  </Selo>
                </p>
                <p className="mt-0.5 line-clamp-2 text-[13px] text-neutral-500">{r.texto}</p>
              </div>
              {editavel(r) && (
                <div className="flex shrink-0 gap-1">
                  <Botao
                    variante="fantasma"
                    tamanho="icone"
                    className="size-8"
                    onClick={() => setEditando(r)}
                    aria-label={`Editar /${r.atalho}`}
                  >
                    <Pencil size={15} strokeWidth={1.75} />
                  </Botao>
                  <Botao
                    variante="fantasma"
                    tamanho="icone"
                    className="size-8 text-danger-500"
                    onClick={() => remover.mutate(r.id)}
                    aria-label={`Remover /${r.atalho}`}
                  >
                    <Trash2 size={15} strokeWidth={1.75} />
                  </Botao>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormResposta({
  inicial,
  podeLoja,
  onFim,
}: {
  inicial: RespostaRapida | null;
  podeLoja: boolean;
  onFim: () => void;
}) {
  const qc = useQueryClient();
  const [atalho, setAtalho] = useState(inicial?.atalho ?? "");
  const [titulo, setTitulo] = useState(inicial?.titulo ?? "");
  const [texto, setTexto] = useState(inicial?.texto ?? "");
  const [pessoal, setPessoal] = useState(inicial?.pessoal ?? !podeLoja);
  const salvar = useMutation({
    mutationFn: () =>
      inicial
        ? api.put(`/respostas-rapidas/${inicial.id}`, { atalho, titulo, texto })
        : api.post("/respostas-rapidas", { atalho, titulo, texto, pessoal }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["respostas-rapidas"] });
      toast.success("Resposta salva!");
      onFim();
    },
    onError: (e) => toast.error(e instanceof ErroApi ? e.message : "Não foi possível salvar."),
  });
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    salvar.mutate();
  };
  return (
    <form onSubmit={enviar} className="space-y-4">
      <Rotulo texto="Atalho" ajuda="Letras minúsculas, números, - ou _. Ex.: pix, prazo, troca.">
        <div className="flex items-center gap-1">
          <span className="text-neutral-500">/</span>
          <Campo
            value={atalho}
            onChange={(e) => setAtalho(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
            required
            maxLength={30}
            autoFocus
          />
        </div>
      </Rotulo>
      <Rotulo texto="Título">
        <Campo value={titulo} onChange={(e) => setTitulo(e.target.value)} required maxLength={80} />
      </Rotulo>
      <Rotulo texto="Mensagem" ajuda="{nome} vira o primeiro nome da cliente; {vendedora}, o seu.">
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          required
          rows={5}
          maxLength={4096}
          className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm shadow-xs"
        />
      </Rotulo>
      {!inicial && podeLoja && (
        <div className="rounded-lg border border-neutral-200 px-3">
          <Interruptor
            ligado={!pessoal}
            onMudar={(v) => setPessoal(!v)}
            rotulo="Disponível para toda a loja"
            descricao="Desligado, só você vê."
          />
        </div>
      )}
      <div className="flex gap-2">
        <Botao type="submit" disabled={salvar.isPending}>
          Salvar
        </Botao>
        <Botao variante="secundario" onClick={onFim}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
